import ActionButtonRounded from 'Components/ActionButtonRounded';
import DataTable from 'Components/DataTable';
import { Container, Item } from 'Components/Grid';
import HistoryRowSubHeader from 'Components/HistoryRowSubHeader';
import InputHandler from 'Components/InputHandler';
import { isPartialDate, partialDateFromDate } from 'neuro-utils';
import { formatPartialDate } from 'Utility/date';
import * as React from 'react';
import { useAppDispatch as useDispatch, useAppSelector, assertCapabilities } from 'Store/index';
import { useMyServiceContext } from '..';
import NewTreatmentDecisionCell from './components/NewTreatmentDecisionCell';
import { getSurveyStatus, surveyCreator, surveyEditor } from 'Routes/MyService/util';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import colors from '../../../../config/theme/colors';
import {
  bai,
  bdi,
  copd,
  deps,
  des,
  deviceTherapyControlVisitInquiry,
  dss,
  ess,
  isi,
  contraIndicationsInquiry,
  ninmtPreInquiry,
  sleepApneaFirstVisitInquiry,
  satisfactionWithCare,
  treatmentMonitoringInquiry,
  eq5d,
  pedsql,
  Task_Progress,
  passQuestion,
  mgadl,
  msnq,
  fsmc,
  mgQol15r,
  fss,
  isMyDocumentType,
} from 'neuro-schemas';
import { IconButton, Menu, MenuItem } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt';
import SentimentSatisfiedIcon from '@mui/icons-material/SentimentSatisfied';
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';
import ConfirmationDialog from 'Components/ConfirmationDialog';
import { actions } from 'Store/myapp/actions';
import type { ICapabilityContextProps } from 'Containers/CapabilityHandler';
import { withCapabilities } from 'Containers/CapabilityHandler';
import PlatformCapabilities from '../../../../config/capabilities';
import { surveysByPlatform } from '../config';
import RecurringTasksList from './components/RecurringTasksList';
import { omitControlProps, sortDocumentsDescending } from 'Utility/documentHandling';
import { capitalize } from 'Utility/string';
import { useNavigate } from 'react-router';
import { blurActiveElement } from 'Utility/domFocus';
import { DateTime } from 'luxon';
import browserLanguage from 'Utility/browserLanguage';
import { TASK_DATE_FORMAT } from 'Utility/tasklist';

const StyledMenuIcon = ({ ...other }) => (
  <IconButton
    disableRipple
    disableFocusRipple
    {...other}
    sx={{ padding: '0 1rem', color: '#6c96ae', cursor: 'pointer' }}
  >
    <MoreVertIcon />
  </IconButton>
);

const satisfactionWithCareIconStyle = {
  color: colors.black,
  background: 'radial-gradient(circle, #F2C900 50%, #00000000 50%)',
};

const SatisfactionWithCareIcon = ({ value }: { value: ISatisfactionWithCare['satisfactionWithCare'] }) =>
  ({
    satisfied: <SentimentSatisfiedAltIcon style={satisfactionWithCareIconStyle} />,
    somewhatSatisfied: <SentimentSatisfiedIcon style={satisfactionWithCareIconStyle} />,
    dissatisfied: <SentimentVeryDissatisfiedIcon style={satisfactionWithCareIconStyle} />,
    unknown: <></>,
  })[value ?? 'unknown'];

const canEdit = (doctype?: TMyDocumentType) =>
  doctype &&
  doctype !== 'pedsql' &&
  doctype !== 'pdq8' &&
  doctype !== 'mgQol15r' &&
  doctype !== 'fss' &&
  doctype !== 'mgFatigueDiary';

const allSurveySchemas = {
  bdi,
  bai,
  ninmtPreInquiry,
  contraIndicationsInquiry,
  treatmentMonitoringInquiry,
  copd,
  deps,
  des,
  dss,
  ess,
  isi,
  sleepApneaFirstVisitInquiry,
  deviceTherapyControlVisitInquiry,
  satisfactionWithCare,
  eq5d,
  pedsql,
  passQuestion,
  mgadl,
  msnq,
  fsmc,
  mgQol15r,
  fss,
};

const DEFAULT_CAPABILITY_GROUPS: IGroupACLMapping = {};
const SurveysHistory = ({
  treatmentDecisionDocs,
  surveyDocs,
  capabilityGroups = DEFAULT_CAPABILITY_GROUPS,
  taskLists,
}: IOwnProps) => {
  const navigate = useNavigate();
  const { fm, setEditingObj, setViewingObj } = useMyServiceContext<TMyDocumentType>();
  const [surveyTypeInput, setSurveyTypeInput] = React.useState<TMyDocumentType | undefined>(undefined);
  const [tableFilterInput, setTableFilterInput] = React.useState<string>('all');

  const allHistoryData = [...treatmentDecisionDocs, ...surveyDocs];

  const users = useAppSelector(s => s.session.orgUsers) ?? [];
  const locale = useAppSelector(s => s.settings.userSettings.uiLanguage) || browserLanguage;
  const platform = useAppSelector(s => s.session.platforms?.selected);
  const mysqUserId = useAppSelector(s => s.myapp.mysqUserId);
  const { delegates = [], informants = [] } = useAppSelector(s => s.patient);

  const onChangeSurveyType = (values: TOnChangeValues): void => {
    const name = Object.keys(values)[0];
    if (!name) return;
    const value = values[name];
    if (isMyDocumentType(value)) {
      setSurveyTypeInput(value as TMyDocumentType);
    }
  };

  const onChangeFilter = (values: TOnChangeValues): void => {
    const name = Object.keys(values)[0];
    if (!name) return;
    const value = values[name];
    if (typeof value === 'string') {
      setTableFilterInput(value);
    }
  };

  const [menuData, setMenuData] = React.useState<{
    anchor: null | HTMLElement;
    data: Record<string, any>;
    type: TMyDocumentType;
  } | null>(null);

  const toggleMenu =
    (data: Record<string, any>, type: TMyDocumentType | undefined) =>
    (e: React.MouseEvent<HTMLElement>): void => {
      if (menuData) blurActiveElement();
      // dumdum
      setMenuData(menuData || !type ? null : { anchor: e.currentTarget, data, type });
    };

  const deletingCapability = assertCapabilities([PlatformCapabilities.MYSERVICE_SURVEYS_DELETION], capabilityGroups);
  const [deleteId, setDeleteId] = React.useState<string | null>(null);

  const historyDataToTable = allHistoryData
    .sort(sortDocumentsDescending)
    // TODO: Hacky...
    .filter(
      d =>
        tableFilterInput === 'all' ||
        d._type === tableFilterInput ||
        (d._type === 'pedsql' &&
          'module' in d &&
          typeof d.module === 'string' &&
          tableFilterInput === `pedsql${capitalize(d.module)}`),
    )
    .map(data => {
      let title;
      if (Object.keys(allSurveySchemas).includes(data._type)) {
        if (data._type === 'pedsql' && 'module' in data && typeof data.module === 'string') {
          title = fm(`myService.${platform}.opts.pedsql${capitalize(data.module)}`);
        } else {
          if (data._type === 'mgadl') {
            title = allSurveySchemas[data._type].localizations[locale].mgadlShort;
          } else {
            title = allSurveySchemas[data._type as keyof typeof allSurveySchemas].localizations[locale]?.title;
          }
        }
      } else {
        if (data._source === 'irom' && data._type === 'memory') {
          title = fm('myService.parkinson.memoryInformant');
        } else {
          title = platform ? fm(`myService.${platform}.${data._type}`) : '-';
        }
      }

      const { yielded, ceiling } = Task_Progress.calculateProgress(data._type, omitControlProps(data));

      if (data._type === 'ninmtTreatmentPeriod' && data.date) {
        return <NewTreatmentDecisionCell fm={fm} date={data.date} key={data._id} />;
      } else {
        return [
          <div key="date" style={{ whiteSpace: 'nowrap', paddingRight: '0.5rem' }}>
            {data._type === 'eq5d'
              ? formatPartialDate(data.date ?? partialDateFromDate(new Date(data._docCreateDate)))
              : formatPartialDate(
                  'date' in data && isPartialDate(data.date)
                    ? data.date
                    : partialDateFromDate(new Date(data._docCreateDate)),
                )}
            {/** TODO ^^ Ugly af, most likely causes problems at some point */}
          </div>,
          title,
          getSurveyStatus(data) ? (
            <div key={'filled_' + data._id} data-surveystatus="filled" style={{ display: 'flex' }}>
              {data._type === 'satisfactionWithCare' || data._type === 'passQuestion' ? (
                <SatisfactionWithCareIcon
                  value={
                    data._type === 'passQuestion'
                      ? data.satisfiedWithCurrentSituation === 'yes'
                        ? 'satisfied'
                        : 'dissatisfied'
                      : data.satisfactionWithCare
                  }
                />
              ) : (
                <React.Fragment>
                  <CheckCircleIcon style={{ marginRight: '0.5rem', color: colors.success }} />
                  {ceiling || ceiling === 0
                    ? data._type === 'pdq8'
                      ? `${Math.round(((typeof yielded === 'number' ? yielded : 1) / ceiling) * 100)} %`
                      : `${yielded} / ${ceiling}`
                    : fm('myService.filled')}
                </React.Fragment>
              )}
            </div>
          ) : getSurveyStatus(data) === false ? (
            <div key={'inProgress_' + data._id} data-surveystatus="inProgress" style={{ display: 'flex' }}>
              <WarningIcon style={{ marginRight: '0.5rem', color: colors.warning.dark }} />
              {fm('myService.inProgress')}
            </div>
          ) : (
            '-'
          ),
          surveyCreator(data, mysqUserId, users, delegates, informants, fm),
          surveyEditor(data, mysqUserId, users, delegates, informants, fm),
          <div key={data._id} style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <>
              {data._type === 'mgFatigueDiary' ? (
                <></>
              ) : (
                <ActionButtonRounded
                  text="general.view"
                  filled={true}
                  width={9.6}
                  height={2.5}
                  fontSize={16}
                  uppercase={false}
                  onClick={() => {
                    if (data._type === 'fbimod' || data._type === 'iadl') {
                      navigate(
                        { pathname: `/diagnosticSurvey` },
                        {
                          state: { myFormDocType: data._type, initData: data, type: 'view' },
                        },
                      );
                    } else {
                      if (!isMyDocumentType(data._type)) return; // log? idk?
                      setViewingObj({ type: data._type, data });
                    }
                  }}
                />
              )}
              <StyledMenuIcon onClick={isMyDocumentType(data._type) ? toggleMenu(data, data._type) : undefined} />
            </>
          </div>,
        ];
      }
    });

  const rowStyleArr: Array<{ rowColor?: string; rowAlign?: string; cellStyle?: Array<React.CSSProperties> }> =
    Array.from({ length: historyDataToTable.length });

  historyDataToTable.forEach((row, i) => {
    if (Array.isArray(row) && row[2]) {
      if (typeof row[2] === 'string') return;
      if (row[2].props['data-surveystatus'] === 'inProgress') rowStyleArr[i] = { rowColor: colors.warning.lightest };
    }
  });

  const dispatch = useDispatch();

  const recurringTaskLists = taskLists.filter(
    t =>
      t.recurrence.recurring &&
      (!t.end_exclusive || DateTime.fromFormat(t.end_exclusive, TASK_DATE_FORMAT) > DateTime.now()) &&
      platform,
    // t.tasks.every(task => surveysByPlatform[platform]?.includes(task)),
  );

  return (
    <>
      <Container style={{ justifyContent: 'space-between', marginBottom: '2rem' }}>
        <Item
          xs={7}
          style={{
            paddingRight: '2rem',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          <HistoryRowSubHeader header={fm('myService.surveys')} />
          {platform && recurringTaskLists.length > 0 && (
            <RecurringTasksList platform={platform} recurringTasks={recurringTaskLists} />
          )}
          <div
            style={{
              width: '100%',
              height: '5.6rem',
              backgroundColor: colors.appBlue.lightest,
              borderRadius: '0.5rem',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <span style={{ color: colors.appBlue.default, paddingLeft: '2rem', marginRight: '1rem' }}>
              {fm('myService.show')}
            </span>
            <div>
              <InputHandler
                type="Select"
                name="tableFilter"
                editing={true}
                options={[
                  'all',
                  ...(platform && surveysByPlatform[platform] && Array.isArray(surveysByPlatform[platform])
                    ? (surveysByPlatform[platform] ?? [])
                        .flatMap(s => (s === 'pedsql' ? ['pedsqlNeuromuscular', 'pedsqlFatigue'] : s))
                        .filter(st =>
                          st.toLowerCase().includes('background') ||
                          st === 'parkinsonLifestyle' ||
                          st === 'supplyShortagesInquiry'
                            ? false
                            : true,
                        )
                    : []),
                ]}
                formData={{ onChange: onChangeFilter, document: { tableFilter: tableFilterInput } }}
                optionFormatter={o => fm(`myService.${platform}.opts.${o}`)}
              />
            </div>
          </div>
        </Item>
        <Item xs={5} style={{ paddingLeft: '2rem' }}>
          <div style={{ border: 'solid 1px', borderColor: colors.gray, borderRadius: '0.3rem', padding: '2rem' }}>
            <span style={{ color: colors.tertiaryText }}>{fm('myService.surveyFillingText')}</span>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.5rem' }}>
              <InputHandler
                type="Select"
                name="surveyTypeInput"
                editing={true}
                options={
                  platform
                    ? surveysByPlatform[platform]
                        ?.flatMap(s => (s === 'pedsql' ? ['pedsqlNeuromuscular', 'pedsqlFatigue'] : s)) // wtf?
                        .filter(st =>
                          st.toLowerCase().includes('background') // ???
                            ? false
                            : platform === 'ninmt'
                              ? st !== 'contraIndicationsInquiry' &&
                                st !== 'supplyShortagesInquiry' &&
                                st !== 'treatmentEfficiency'
                              : platform === 'sma' || platform === 'dmd'
                                ? st !== 'pedsqlNeuromuscular' && st !== 'pedsqlFatigue'
                                : platform === 'parkinson'
                                  ? st !== 'pdq8' && st !== 'parkinsonLifestyle' // what the hell is going on??
                                  : platform === 'mgravis'
                                    ? st !== 'mgadl' && st !== 'mgQol15r' && st !== 'fss'
                                    : st,
                        )
                    : []
                }
                optionFormatter={o => fm(`myService.${platform}.opts.${o}`)}
                formData={{ onChange: onChangeSurveyType, document: { surveyTypeInput } }}
                placeholder={'myService.chooseSurvey'}
              />
              <ActionButtonRounded
                text="myService.fillSurvey"
                filled={true}
                uppercase={false}
                width={9.6}
                height={3}
                fontSize={16}
                disabled={surveyTypeInput === undefined}
                onClick={() => {
                  if (surveyTypeInput) {
                    setEditingObj({ type: surveyTypeInput, data: {} });
                  }
                }}
              />
            </div>
          </div>
        </Item>
      </Container>
      <DataTable
        headers={['date', 'name', 'statusAndScore', 'reporter', 'edited', 'hidden']}
        headersFormatter={h =>
          h === 'hidden'
            ? ''
            : h === 'statusAndScore'
              ? `${fm('myService.status')} / ${fm('myService.score')}`
              : fm(`myService.${h}`)
        }
        data={{ rowData: historyDataToTable, rowStyle: rowStyleArr }}
      />

      <Menu
        id="surveyhistorymenu"
        anchorEl={menuData?.anchor}
        open={Boolean(menuData)}
        onClose={toggleMenu({}, undefined)}
      >
        <MenuItem
          onClick={() => {
            if (!menuData?.type) return;
            if (menuData.type === 'fbimod' || menuData.type === 'iadl') {
              navigate(
                { pathname: `/diagnosticSurvey` },
                { state: { myFormDocType: menuData.type, initData: menuData.data, type: 'edit' } },
              );
            }
            setEditingObj({ type: menuData.type, data: menuData.data as any });
          }}
          disabled={!canEdit(menuData?.type)}
        >
          {fm('general.edit')}
        </MenuItem>
        {deletingCapability ? (
          <MenuItem
            onClick={e => {
              setDeleteId(menuData?.data._id);
              toggleMenu({}, undefined)(e);
            }}
          >
            {fm('general.delete')}
          </MenuItem>
        ) : null}
      </Menu>
      <ConfirmationDialog
        open={!!deleteId}
        text={fm('general.reallyWantToDelete')}
        cancel={{ callback: () => setDeleteId(null) }}
        confirm={{
          callback: () => {
            const deleteSurvey = allHistoryData.find(d => d._id === deleteId);
            deleteSurvey && actions.deleteDocument(deleteSurvey._type, deleteSurvey._id, dispatch);
            setDeleteId(null);
          },
        }}
      />
    </>
  );
};

interface IOwnProps extends ICapabilityContextProps {
  treatmentDecisionDocs: IDocumentWithTypeV2<'ninmtTreatmentPeriod'>[];
  surveyDocs: Array<IDocumentWithTypeV2<TMyDocumentType>>;
  onGoingTasks: Array<ITaskList>;
  taskLists: ITaskList[];
}

export default withCapabilities(SurveysHistory);
